import React, { useState, useContext } from "react";
import { CurrencyContext } from "../../context/CurrencyContext";
import { useMyContext } from "../../context/MyContext";
import { toast } from "react-toastify";

const currencyOptions = [
  { name: "USD", symbol: "$" },
  { name: "EUR", symbol: "€" },
  { name: "GBP", symbol: "£" },
];

const UpdateCurrency = ({ setShowHistory }) => {
  const { addCurrency } = useContext(CurrencyContext);
  const { user } = useMyContext();
  const [showModal, setShowModal] = useState(false);
  const [currencyData, setCurrencyData] = useState({
    name: "",
    symbol: "",
    exchangeRates: {
      USD: "",
      EUR: "",
      GBP: ""
    }
  });
  const [loading, setLoading] = useState(false);

  const handleCurrencyChange = (e) => {
    const selectedCurrency = currencyOptions.find(
      (currency) => currency.name === e.target.value
    );

    // Reset exchange rates and set the selected currency rate to 1
    const newExchangeRates = { USD: "", EUR: "", GBP: "" };
    newExchangeRates[selectedCurrency.name] = "1";

    setCurrencyData({
      name: selectedCurrency.name,
      symbol: selectedCurrency.symbol,
      exchangeRates: newExchangeRates
    });
  };

  const handleExchangeRateChange = (targetCurrency, value) => {
    setCurrencyData(prev => ({
      ...prev,
      exchangeRates: {
        ...prev.exchangeRates,
        [targetCurrency]: value
      }
    }));
  };

  const resetForm = () => {
    setCurrencyData({
      name: "",
      symbol: "",
      exchangeRates: {
        USD: "",
        EUR: "",
        GBP: ""
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate that at least one exchange rate is provided (other than the base currency)
      const nonBaseCurrencies = Object.keys(currencyData.exchangeRates).filter(
        curr => curr !== currencyData.name
      );

      const hasValidRate = nonBaseCurrencies.some(
        curr => currencyData.exchangeRates[curr] &&
          parseFloat(currencyData.exchangeRates[curr]) > 0
      );

      if (!hasValidRate) {
        toast.error("Please provide at least one exchange rate.");
        setLoading(false);
        return;
      }

      // Convert string rates to numbers, keeping null for empty values
      const processedExchangeRates = {};
      Object.keys(currencyData.exchangeRates).forEach(curr => {
        const rate = currencyData.exchangeRates[curr];
        if (curr === currencyData.name) {
          processedExchangeRates[curr] = 1; // Base currency is always 1
        } else if (rate && !isNaN(parseFloat(rate))) {
          processedExchangeRates[curr] = parseFloat(rate);
        } else {
          processedExchangeRates[curr] = null;
        }
      });

      const response = await addCurrency({
        name: currencyData.name,
        exchangeRates: processedExchangeRates,
        createdBy: user.uid,
      });

      if (response && response.status === 201) {
        toast.success("Currency added successfully!");
        setShowModal(false);
        resetForm();
      } else {
        toast.error("Failed to add currency. Please try again.");
      }
    } catch (error) {
      console.log(error);
      if (error?.response?.data?.message?.includes("already exists")) {
        toast.warning("Currency already exists. You can edit it instead.");
      } else {
        toast.error("Failed to add currency.");
      }
      resetForm();
    } finally {
      setLoading(false);
    }
  };

  const renderExchangeRateInputs = () => {
    if (!currencyData.name) return null;

    return currencyOptions
      .filter(option => option.name !== currencyData.name)
      .map(option => (
        <div key={option.name}>
          <label className="block font-medium">
            Exchange Rate to {option.name} ({option.symbol}):
          </label>
          <input
            type="number"
            step="any"
            value={currencyData.exchangeRates[option.name]}
            onChange={(e) => handleExchangeRateChange(option.name, e.target.value)}
            placeholder={`1 ${currencyData.name} = ? ${option.name}`}
            className="w-full p-2 border rounded-md"
          />
        </div>
      ));
  };

  return (
    <div className="w-full p-4">
      <div className="flex w-full justify-between mb-4">
        <button
          onClick={() => setShowHistory(true)}
          className="px-4 py-2 text-blue-600 border-blue-600 btnBg flex rounded-[5px]"
        >
          Check Currency History
        </button>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 text-blue-600 border-blue-600 btnBg flex rounded-[5px]"
        >
          Add New Currency
        </button>
      </div>

      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          onClick={() => setShowModal(false)}
        >
          <div
            className="relative bg-white p-6 rounded-lg shadow-lg w-96 z-50 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-3 right-7 text-gray-500 text-2xl hover:text-red-500"
            >
              &times;
            </button>

            <h2 className="text-lg font-semibold mb-4">Add New Currency</h2>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block font-medium">Base Currency:</label>
                <select
                  name="name"
                  value={currencyData.name}
                  onChange={handleCurrencyChange}
                  className="w-full p-2 border rounded-md"
                  required
                >
                  <option value="" disabled>Select Base Currency</option>
                  {currencyOptions.map((currency) => (
                    <option key={currency.name} value={currency.name}>
                      {currency.name} ({currency.symbol})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-medium">Symbol:</label>
                <input
                  type="text"
                  name="symbol"
                  value={currencyData.symbol}
                  readOnly
                  className="w-full p-2 border rounded-md bg-gray-100"
                />
              </div>

              {renderExchangeRateInputs()}

              {currencyData.name && (
                <div className="bg-blue-50 p-3 rounded-md">
                  <p className="text-sm text-blue-700">
                    <strong>Note:</strong> You are setting exchange rates FROM {currencyData.name} TO other currencies.
                    For example, if 1 {currencyData.name} = 1.2 USD, enter 1.2 in the USD field.
                  </p>
                </div>
              )}

              <div className="flex justify-start gap-2 mt-4">
                <button
                  type="submit"
                  className="px-4 py-2 text-blue-600 border-blue-600 btnBg flex rounded-[5px]"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-300 rounded-md"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdateCurrency;